/*!
 * Amplitude — module Sommaire
 * © 2026 Laurent Allègre — AL Consulting Solutions (al-consulting-solutions.fr). Tous droits réservés.
 * Code propriétaire : toute reproduction ou réutilisation hors licence écrite est interdite.
 */
/**
 * Sommaire automatique (snippet toc) : liste les H2 du conteneur désigné par
 * data-toc-source, génère les ids manquants, suit la section visible au
 * défilement (lien actif) et gère l'état replié/déplié selon la largeur
 * (details fermé sous 800 px, ouvert au-dessus).
 */
const DESKTOP_QUERY = '(min-width: 800px)';

class TocComponent extends HTMLElement {
  #observer;
  #mediaQuery;
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();

    const source = this.dataset.tocSource || '.rte';
    const container = document.querySelector(source);
    const list = this.querySelector('.toc__list');
    const details = this.querySelector('.toc__details');
    if (!container || !list || !details) return;

    const headings = Array.from(container.querySelectorAll('h2'));
    if (headings.length < 2) return;

    const usedIds = new Set();
    for (const heading of headings) {
      if (!heading.id) {
        heading.id = this.#uniqueId(heading.textContent, usedIds);
      }
      usedIds.add(heading.id);

      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      item.appendChild(link);
      list.appendChild(item);
    }

    this.hidden = false;

    this.#mediaQuery = window.matchMedia(DESKTOP_QUERY);
    const syncOpen = () => {
      details.open = this.#mediaQuery.matches;
    };
    syncOpen();
    this.#mediaQuery.addEventListener('change', syncOpen, { signal: this.#abortController.signal });

    this.#observer = new IntersectionObserver((entries) => this.#onIntersect(entries), {
      rootMargin: '0px 0px -70% 0px',
    });
    for (const heading of headings) this.#observer.observe(heading);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abortController?.abort();
  }

  /**
   * Fabrique un id unique et lisible depuis le texte d'un titre.
   * @param {string} text
   * @param {Set<string>} used
   */
  #uniqueId(text, used) {
    const base =
      (text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60) || 'section';

    let id = base;
    let counter = 2;
    while (used.has(id) || document.getElementById(id)) {
      id = `${base}-${counter}`;
      counter += 1;
    }
    return id;
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect(entries) {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const link = this.querySelector(`.toc__list a[href="#${CSS.escape(entry.target.id)}"]`);
      if (!link) continue;
      for (const active of this.querySelectorAll('.toc__list a[aria-current]')) {
        active.removeAttribute('aria-current');
      }
      link.setAttribute('aria-current', 'true');
    }
  }
}

if (!customElements.get('toc-component')) {
  customElements.define('toc-component', TocComponent);
}
